import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactNode, useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { closeOnly } from '@/components/dialogs/close-only';
import { DatePicker } from '@/components/ui/date-picker';
import { FieldError } from '@/components/ui/field-error';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useInstruments } from '@/hooks/queries/markets/use-markets';
import { useQuoteSources } from '@/hooks/queries/mm-programs/use-mm-programs';
import { toDisplayState } from '@/services/api/markets';
import { uniqueSorted } from '@/utils/array';
import { pluralize } from '@/utils/string';
import { todayUtc } from '../date-bounds';
import { feedsByInstrument, marketMakerOptions, PROGRAM_TYPE_LABELS } from '../mm-program-editing';
import { PROGRAM_TYPES } from '../mm-program-schema';
import { type AddSchedulesResult, addSchedules, currentOf, type GridDocument } from './grid-rows';
import { editableOf } from './use-program-drafts';

const KEEP_VARIANT = 'keep';
const HAS_SCHEDULES = 'Has schedules';

const addProgramsSchema = z.object({
  pid: z.string().min(1, 'Pick a market maker.'),
  instruments: z.array(z.string()).min(1, 'Pick at least one instrument.'),
  start: z.string().min(1, 'Pick the first day.'),
  variant: z.union([z.literal(KEEP_VARIANT), z.enum(PROGRAM_TYPES)]),
});

type AddProgramsSchema = z.infer<typeof addProgramsSchema>;

interface FieldRowProps {
  id: string;
  label: string;
  error?: string;
  children: ReactNode;
}

/** A label beside its field, and the field's problem under it. */
const FieldRow = ({ id, label, error, children }: FieldRowProps) => (
  <>
    <Label htmlFor={id}>{label}</Label>
    {children}
    {error ? (
      <div className="col-start-2 -mt-1.5">
        <FieldError message={error} />
      </div>
    ) : null}
  </>
);
FieldRow.displayName = 'FieldRow';

interface AddProgramsDialogProps {
  documents: GridDocument[];
  onAdd: (pid: string, result: AddSchedulesResult) => void;
  onClose: () => void;
}

/**
 * Starts a schedule on one day for several instruments of one market maker. The schedules land in the grid as unsaved
 * rows to fill in, so their terms are entered, checked and saved like any other grid edit.
 */
export const AddProgramsDialog = ({ documents, onAdd, onClose }: AddProgramsDialogProps) => {
  const markets = useInstruments();
  const quoteSources = useQuoteSources();
  const form = useForm<AddProgramsSchema>({
    resolver: zodResolver(addProgramsSchema),
    defaultValues: { pid: '', instruments: [], start: todayUtc(), variant: KEEP_VARIANT },
    mode: 'onChange',
  });
  const { errors } = form.formState;
  const { pid, instruments, start, variant } = form.watch();
  const marketMakerId = useId();
  const instrumentsId = useId();
  const startId = useId();
  const variantId = useId();

  const editable = documents.filter((document) => document.canEdit);
  const makerOptions = marketMakerOptions(editable.map((document) => ({ pid: document.pid, ...currentOf(document) })));
  const picked = editable.find((document) => document.pid === pid);
  const draft = picked ? editableOf(picked) : undefined;
  const existing = new Set(Object.keys(draft?.programs ?? {}));
  const states = new Map((markets.data ?? []).map((market) => [market.id, toDisplayState(market.state)]));
  // bop-api refuses an instrument the exchange does not list, unless the market maker already has it.
  const instrumentOptions = uniqueSorted([...states.keys(), ...existing]).map((id) => ({
    value: id,
    label: id,
    detail: existing.has(id) ? HAS_SCHEDULES : states.get(id),
  }));
  // Equity futures trade around US market hours, so a new one starts with a bucket set per session.
  const sessioned = new Set((markets.data ?? []).filter((market) => market.type === 'EQUITY_FUTURE').map((market) => market.id));
  const feeds = feedsByInstrument(quoteSources.data?.sources ?? []);

  const result = draft && instruments.length && start ? addSchedules(draft, { instruments, start, type: variant === KEEP_VARIANT ? undefined : variant, sessioned, feeds }) : null;

  const handleAdd = form.handleSubmit((values) => {
    if (result?.added) onAdd(values.pid, result);
  });

  return (
    <Dialog onOpenChange={closeOnly(onClose)} open>
      <DialogContent aria-describedby={undefined} className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add programs</DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" noValidate onSubmit={handleAdd}>
          <div className="grid grid-cols-[8rem_minmax(0,1fr)] items-center gap-x-3 gap-y-3 text-sm">
            <FieldRow error={errors.pid?.message} id={marketMakerId} label="Market maker">
              <Controller
                control={form.control}
                name="pid"
                render={({ field, fieldState }) => (
                  <SearchableSelect
                    aria-invalid={fieldState.invalid}
                    empty="No market makers found"
                    id={marketMakerId}
                    onChange={field.onChange}
                    options={makerOptions}
                    placeholder="Pick a market maker"
                    searchPlaceholder="Search market makers…"
                    value={field.value}
                  />
                )}
              />
            </FieldRow>
            <FieldRow error={errors.instruments?.message} id={instrumentsId} label="Instruments">
              <Controller
                control={form.control}
                name="instruments"
                render={({ field, fieldState }) => (
                  <SearchableMultiSelect
                    aria-invalid={fieldState.invalid}
                    disabled={markets.isPending}
                    empty="No markets found"
                    id={instrumentsId}
                    onChange={field.onChange}
                    options={instrumentOptions}
                    placeholder={markets.isPending ? 'Loading markets…' : 'Pick instruments'}
                    searchPlaceholder="Search markets…"
                    value={field.value}
                  />
                )}
              />
            </FieldRow>
            <FieldRow error={errors.start?.message} id={startId} label="Start (UTC)">
              <Controller
                control={form.control}
                name="start"
                render={({ field, fieldState }) => <DatePicker aria-invalid={fieldState.invalid} id={startId} onChange={field.onChange} value={field.value} />}
              />
            </FieldRow>
            <FieldRow error={errors.variant?.message} id={variantId} label="Variant">
              <Controller
                control={form.control}
                name="variant"
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger id={variantId}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={KEEP_VARIANT}>Same as the schedule it follows</SelectItem>
                      {PROGRAM_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {PROGRAM_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </FieldRow>
          </div>
          {markets.isError ? (
            <p className="text-xs text-muted-foreground">The exchange's markets could not load, so only instruments the market maker already has are listed.</p>
          ) : null}
          {result ? (
            <div className="flex flex-col gap-1 rounded-md border border-border bg-muted/30 p-3 text-sm">
              <p>
                Adds {pluralize(result.added, 'schedule')}
                {result.ended ? ` and ends ${pluralize(result.ended, 'open schedule')} on ${start}` : ''}.
              </p>
              {result.skipped.length ? (
                <>
                  <p className="text-muted-foreground">Skips {pluralize(result.skipped.length, 'instrument')}:</p>
                  <ul className="max-h-32 list-disc overflow-y-auto pl-5 text-xs text-muted-foreground">
                    {result.skipped.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            {/* Every instrument skipped leaves nothing to add; the summary above says why. */}
            <Button disabled={result !== null && !result.added} type="submit" variant="primary">
              Add {result?.added ? pluralize(result.added, 'schedule') : 'schedules'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
AddProgramsDialog.displayName = 'AddProgramsDialog';
